import React from 'react';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Spinner } from '../../../shared/components';
import { useAssetSettings } from '../context/AssetSettingsContext';
import { getAssetById } from '../services/assetService';
import { assetTypePath, getAssetTypeBySlug, isAssetIdParam } from '../typeCatalog';

export const LegacyAssetAddRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { enabledTypes, loading } = useAssetSettings();

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}><Spinner /></div>;
  }

  const first = enabledTypes[0];
  if (!first) {
    return <Navigate to="/assets/settings" replace />;
  }

  const query = searchParams.toString();
  return <Navigate to={`${assetTypePath(first, '/add')}${query ? `?${query}` : ''}`} replace />;
};

export const AssetTypeRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { typeSlug } = useParams<{ typeSlug: string }>();
  const location = useLocation();

  if (isAssetIdParam(typeSlug)) {
    return <AssetIdRedirect id={typeSlug!} suffix={location.pathname.endsWith('/details') ? '/details' : ''} search={location.search} />;
  }

  const module = getAssetTypeBySlug(typeSlug);
  if (!module) {
    return <Navigate to="/assets" replace />;
  }

  if (typeSlug !== module.slug) {
    const rest = location.pathname.replace(`/assets/${typeSlug}`, `/assets/${module.slug}`);
    return <Navigate to={`${rest}${location.search}`} replace />;
  }

  return children;
};

export const AssetTypeListRedirect: React.FC = () => {
  const { typeSlug } = useParams<{ typeSlug: string }>();
  const location = useLocation();

  if (isAssetIdParam(typeSlug)) {
    return <AssetIdRedirect id={typeSlug!} suffix={location.pathname.endsWith('/details') ? '/details' : ''} search={location.search} />;
  }

  const module = getAssetTypeBySlug(typeSlug);
  if (!module) {
    return <Navigate to="/assets" replace />;
  }

  return <Navigate to={`/assets?type=${module.slug}`} replace />;
};

const AssetIdRedirect: React.FC<{ id: string; suffix: string; search: string }> = ({ id, suffix, search }) => {
  const [target, setTarget] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;
    void getAssetById(id)
      .then(asset => {
        if (!mounted) {
          return;
        }
        setTarget(asset ? `${assetTypePath(asset.assetType, `/${asset.id}`)}${suffix}${search}` : '/assets');
      })
      .catch(() => {
        if (mounted) {
          setTarget('/assets');
        }
      });
    return () => {
      mounted = false;
    };
  }, [id, search, suffix]);

  if (!target) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}><Spinner /></div>;
  }

  return <Navigate to={target} replace />;
};
